/**
 * original source: https://ui.shadcn.com/docs/components/sheet
 */
import { XIcon } from 'lucide-react'
import { Dialog as SheetPrimitive } from 'radix-ui'

import { cn, focusRingClassName, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { IconButton } from './Button'
import { isSonnerInteractionTarget } from '../utils/isSonnerInteractionTarget'
import type { ComponentProps } from 'react'

export type SheetProps = ComponentProps<typeof SheetPrimitive.Root>
export const Sheet = SheetPrimitive.Root

export const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

export function SheetCloseIconButton(
  props: Omit<ComponentProps<typeof IconButton>, 'children'>,
) {
  return (
    <SheetClose asChild>
      <IconButton variant="ghost" tooltip="Close" {...props}>
        <XIcon className="size-4" />
      </IconButton>
    </SheetClose>
  )
}

const SheetPortal = SheetPrimitive.Portal

const sheetOverlayVariants = tv({
  base: 'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-40',

  defaultVariants: {
    variant: 'light',
  },

  variants: {
    variant: {
      dark: 'bg-black/80',
      light: 'bg-tremor-background/80',
    },
  },
})

export type SheetOverlayProps = ComponentProps<typeof SheetPrimitive.Overlay> &
  VariantProps<typeof sheetOverlayVariants>

function SheetOverlay({ className, variant, ...props }: SheetOverlayProps) {
  return (
    <SheetPrimitive.Overlay
      className={cn(
        sheetOverlayVariants({
          variant,
        }),
        className,
      )}
      {...props}
    />
  )
}

const sheetVariants = tv({
  base: [
    'fixed z-40 gap-4 bg-tremor-background dark:bg-tremor-background-muted p-6 shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-150',
    'outline-hidden',
    focusRingClassName,
  ],

  defaultVariants: {
    side: 'right',
  },
  variants: {
    side: {
      bottom:
        'inset-x-0 bottom-0 border-t border-tremor-border data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
      left: 'inset-y-0 left-0 h-full w-3/4 border-r border-tremor-border data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
      right:
        'inset-y-0 right-0 h-full w-3/4 border-l border-tremor-border data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
      top: 'inset-x-0 top-0 border-b border-tremor-border data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
    },
  },
})

interface SheetContentProps
  extends
    ComponentProps<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  overlayProps?: SheetOverlayProps
}

export function SheetContent({
  children,
  className,
  onInteractOutside,
  onPointerDownOutside,
  overlayProps,
  side = 'right',
  ...props
}: SheetContentProps) {
  const handleInteractOutside: SheetContentProps['onInteractOutside'] = (
    event,
  ) => {
    if (isSonnerInteractionTarget(event.target)) {
      event.preventDefault()
      return
    }

    onInteractOutside?.(event)
  }

  const handlePointerDownOutside: SheetContentProps['onPointerDownOutside'] = (
    event,
  ) => {
    if (isSonnerInteractionTarget(event.target)) {
      event.preventDefault()
      return
    }

    onPointerDownOutside?.(event)
  }

  return (
    <SheetPortal>
      <SheetOverlay {...overlayProps} />
      <SheetPrimitive.Content
        className={cn(sheetVariants({ side }), className)}
        {...props}
        onInteractOutside={handleInteractOutside}
        onPointerDownOutside={handlePointerDownOutside}
      >
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

export function SheetHeader({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col gap-y-2 text-center sm:text-left',
        className,
      )}
      {...props}
    />
  )
}

export function SheetTitle({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      className={cn('text-foreground text-lg font-semibold', className)}
      {...props}
    />
  )
}

export function SheetDescription({
  className,
  ...props
}: ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}
